// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTagManagementData } from '../providers';
import { tagsApi } from '@shared/data-access/api/engine/tags-api';
import { generateAssignmentId } from '@shared/data-access/slices/tags-management';
import { appSelect, useSnackbar } from '@shared/ui-kit';
import type {
  TagEntity,
  TagsByObjectKey,
} from '@shared/data-access/interfaces/redux/tags-management-state-types';
import type { TagOwnerType } from '@shared/data-access/types/engine-types';

const ownerType = appSelect({
  advisor: 'USER',
  client: 'CLIENT',
}) as TagOwnerType;

export interface UseObjectTagManagementParams {
  objectType: string;
  objectId: string;
  onClose: () => void;
}

export interface ObjectTagManagementResult {
  tags: readonly TagEntity[];
  initialAssignedTags: readonly TagEntity[];
  onChangeTagIds: (ids: number[]) => void;
  onCreateTag: (name: string) => void;
  onCloseWithSync: () => void;
}

export const useObjectTagManagement = ({
  objectType,
  objectId,
  onClose,
}: UseObjectTagManagementParams): ObjectTagManagementResult => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { tags, tagsByObject } = useTagManagementData();
  const [createTag] = tagsApi.useCreateTagMutation();
  const [syncTags] = tagsApi.useSyncTagsMutation();

  const objectKey = useMemo(
    (): TagsByObjectKey =>
      generateAssignmentId(objectType, objectId) as TagsByObjectKey,
    [objectType, objectId],
  );

  const initialAssignedTags = useMemo(
    () => tagsByObject.get(objectKey)?.tags ?? [],
    [tagsByObject, objectKey],
  );

  const [pendingTagIds, setPendingTagIds] = useState<number[]>(() =>
    initialAssignedTags.map(t => t.tag_id),
  );

  const onCreateTag = useCallback(
    (name: string) => {
      createTag({ owner_type: ownerType, name })
        .unwrap()
        .then(tag => {
          setPendingTagIds(prev => [...prev, tag.tag_id]);

          return syncTags({
            owner_type: ownerType,
            object_type: objectType,
            object_id: objectId,
            tag_ids: [...pendingTagIds, tag.tag_id],
          }).unwrap();
        })
        .catch(() => undefined);
    },
    [createTag, syncTags, objectType, objectId, pendingTagIds],
  );

  const onCloseWithSync = useCallback(() => {
    const initialIds = initialAssignedTags.map(t => t.tag_id).sort((a, b) => a - b);
    const pendingSorted = [...pendingTagIds].sort((a, b) => a - b);
    const hasChange =
      initialIds.length !== pendingSorted.length ||
      initialIds.some((id, i) => id !== pendingSorted[i]);

    if (!hasChange) {
      onClose();
      return;
    }

    onClose();

    const key = enqueueSnackbar('', {
      variant: 'syncingTags',
      persist: true,
    });

    syncTags({
      owner_type: ownerType,
      object_type: objectType,
      object_id: objectId,
      tag_ids: [...pendingTagIds],
    })
      .unwrap()
      .then(() => closeSnackbar(key))
      .catch(() => closeSnackbar(key));
  }, [
    enqueueSnackbar,
    closeSnackbar,
    syncTags,
    onClose,
    objectType,
    objectId,
    pendingTagIds,
    initialAssignedTags,
  ]);

  useEffect(() => {
    setPendingTagIds(initialAssignedTags.map(t => t.tag_id));
  }, [initialAssignedTags]);

  return {
    tags,
    initialAssignedTags,
    onChangeTagIds: setPendingTagIds,
    onCreateTag,
    onCloseWithSync,
  };
};
